<template>
  <rt-dialog
    ref="dialog"
    v-model="visible"
    :append-to-body="true"
    :title="title"
    class="lookup-dialog"
    :height="computedHeight"
    :size="size"
    hide-divider
    show-column
    :slide-layout="column ? 'column' : ''"
    :column-width-ratio="ratio"
  >
    <div class="lookup-row">
      <rt-node :show-icon="false">
        <div
          ref="node-header"
          class="lookup-node-header"
        >
          <rt-radio-group
            v-if="entityLabels.length > 0"
            v-model="entityIndex"
            @change="changeRadioHandler"
          >
            <rt-radio-button
              v-for="(entryLabel, index) in entityLabels"
              :key="index"
              :label="index"
            >
              {{ entryLabel }}
            </rt-radio-button>
          </rt-radio-group>

          <div
            ref="node-header-right"
            class="lookup-node-header-right"
          >
            <rt-search
              v-if="tableSearch"
              v-model="queryValue"
              :clearable="clearable"
              :more="false"
              :placeholder="searchPlaceholder"
              @on-search="search"
              @clear="clearQueryValue"
            />
            <rt-checkbox
              v-if="multiple && showSelected && !column"
              v-model="isChecked"
              @change="changeCheckboxHandler"
            >
              {{ $tx('仅显示已选项', 'xpc.lookup.showSelected') }}
            </rt-checkbox>
          </div>
        </div>
        <rt-table
          ref="table"
          :keep-selected="keepSelected"
          :fetch="fetchData"
          :page-size="10"
          :class="{
            'is-single': !multiple,
            'table-footer-shadow': tableFooterShadow
          }"
          :total-record="totalRecords"
          :row-key="getRowKeys"
          :radio="!multiple"
          page-layout="total, sizes, prev, pager, next"
          @select="select"
          @select-all="selectAll"
          @row-click="onTableRowClick"
          @load-complete="onTableLoadComplete"
          @clear-selection="clearSelection"
        >
          <rt-table-column
            v-for="(item, index) in computedDisplayFields"
            :key="index"
            :prop="item.prop"
            :label="item.label"
            :width="item.width"
          />
        </rt-table>
      </rt-node>
    </div>

    <div
      slot="column"
      class="right-column"
    >
      <div
        v-if="multiple"
        ref="lookupDrawer"
        class="column-wrapper"
        :class="[
          column ? 'column-card' : '',
          hideDrawer ? 'column-no-bg' : 'column-bg'
        ]"
      >
        <rt-search
          v-if="drawerSearch"
          v-model="selectValue"
          :more="false"
          :clearable="clearable"
          class="select-search"
          @on-search="searchSelect"
          @clear="clearSearchValue"
        />
        <div class="select-title">
          <div
            v-if="isSearch"
            class="select-num"
          >
            {{
              $tx(
                `已选择 ${selectList.length} 项，为你搜到 ${searchList.length} 项`,
                'xpc.lookup.searchItems',
                {
                  count: selectList.length,
                  search: searchList.length
                }
              )
            }}
          </div>
          <div
            v-else
            class="select-num"
          >
            {{
              $tx(
                `已选择 ${selectList.length} 项`,
                'xpc.lookup.selectedItems',
                {
                  count: selectList.length
                }
              )
            }}
          </div>

          <rt-popconfirm
            v-if="searchList.length > 0"
            :title="$tx('确定需要全部删除吗？', 'xpc.common.confirmDeleteAll')"
            @confirm="clear"
          >
            <rt-button
              slot="reference"
              text
              type="plain"
            >
              {{ $tx('一键清空', 'xpc.dialog.oneClickClear') }}
            </rt-button>
          </rt-popconfirm>
        </div>

        <div
          class="selected-content"
          :class="{ 'selected-content-default-image': !selectList.length }"
        >
          <div
            v-for="(item, index) in searchList"
            :key="item[alias.id]"
            class="selected"
          >
            <div v-html="formatItemHandler(item)"></div>
            <rt-popconfirm
              :title="
                $tx('确定需要删除这条内容吗？', 'xpc.common.comfirmDelete')
              "
              @confirm="delTag(index, item)"
            >
              <rt-icon
                slot="reference"
                name="rt-base-close-x"
              >
              </rt-icon>
            </rt-popconfirm>
          </div>
          <rt-default-image v-show="selectList.length === 0">
            <div slot="describe">
              {{ $tx('暂无数据', 'xpc.uploadfile.nodata') }}
            </div>
          </rt-default-image>
        </div>
      </div>
    </div>

    <div
      slot="footer-left"
      class="rt-dialog-footer-left"
    >
      <slot name="footer-left" />
    </div>
    <div
      slot="footer-right"
      class="rt-dialog-footer-right"
    >
      <rt-button
        type="primary"
        @click="confirm"
      >
        {{ $tx('确认', 'xpc.dialog.confirm') }}
      </rt-button>
    </div>
  </rt-dialog>
</template>
<script>
import RtSearch from '../search/rtSearch';
import RtTable from '../table/rtTable';
import RtTableColumn from '../table/rtTableColumn';
import RtButton from '../button/rtButton.vue';
import RtDefaultImage from '../defaultImage/rtDefaultImage.vue';
import RtDialog from '../dialog/rtDialog';
import RtRadioGroup from '../radio/rtRadioGroup.vue';
import RtRadioButton from '../radio/rtRadioButton.vue';
import RtCheckbox from '../checkbox/rtCheckbox.vue';
import RtNode from '../node/rtNode.vue';
import RtPopconfirm from '../popconfirm/rtPopconfirm';

const copyObj = obj => (obj ? JSON.parse(JSON.stringify(obj)) : '');

const requestAlias = {
  pageSize: 'pageSize',
  pageIndex: 'pageIndex',
  queryValue: 'queryValue',
  entity: 'entity'
};

export default {
  name: 'RtLookupView',
  components: {
    RtSearch,
    RtTable,
    RtTableColumn,
    RtButton,
    RtDefaultImage,
    RtDialog,
    RtRadioGroup,
    RtRadioButton,
    RtCheckbox,
    RtNode,
    RtPopconfirm
  },
  props: {
    column: {
      type: Boolean,
      default: false
    },
    // 左右布局比例
    ratio: {
      type: String,
      default: '2:1'
    },
    // 按钮文字隐藏
    unfoldText: {
      type: Boolean,
      default: true
    },
    drawerSearch: {
      type: Boolean,
      default: false
    },
    tableSearch: {
      type: Boolean,
      default: true
    },
    fetch: {
      type: Function,
      required: true
    },
    alias: {
      type: Object
    },
    requestAlias: {
      type: Object
    },
    multiple: {
      type: Boolean,
      default: false
    },
    displayFields: {
      type: Array,
      default () {
        return [];
      }
    },
    title: String,
    /**
     * @public 表格数据总数
     */
    total: Number,
    /**
     * 是否需要保持选中项在翻页的时候
     */
    keepSelected: {
      type: Boolean,
      default: true
    },
    list: {
      type: Array,
      default () {
        return [];
      }
    },
    clearable: {
      type: Boolean,
      default: true
    },
    /**
     * @public 弹窗尺寸
     */
    size: {
      type: String,
      default: 'large'
    },
    /**
     * @public 请求参数的字段名
     */
    pageFields: {
      type: Object,
      default () {
        return {};
      }
    },
    searchPlaceholder: {
      type: String,
      default () {
        return this.$tx('请搜索', 'xpc.common.pleaseSearch');
      }
    },
    showSelected: {
      type: Boolean,
      default: true
    },
    entities: {
      type: Array,
      default () {
        return [];
      }
    },
    formatItem: {
      type: Function
    }
  },
  data () {
    return {
      queryValue: '',
      entityList: '',
      hideDrawer: false,
      selectList: [...this.list],
      selectValue: '',
      deleteList: [],
      // 搜索后 tag
      searchList: [],
      // 是否搜索
      isSearch: false,
      // dialog是否显示
      visible: false,
      isChecked: false, // 是否仅显示已选项
      entityIndex: '',
      entityLabels: [], // 实体 label
      entityDisplayFields: [], // 实体 displayFields
      tableFooterShadow: false,
      totalRecords: this.total,
      params: {}
    };
  },
  computed: {
    computedHeight () {
      const result = {
        large: '60%',
        normal: '50%',
        small: '30%'
      };
      return result[this.size] || '60%';
    },
    computedRequestAlias () {
      return { ...requestAlias, ...this.requestAlias };
    },
    innerPageFields () {
      return {
        pageSize: this.computedRequestAlias['pageSize'],
        pageIndex: this.computedRequestAlias['pageIndex'],
        queryValue: this.computedRequestAlias['queryValue'],
        entity: this.computedRequestAlias['entity'],
        ...this.pageFields
      };
    },
    computedDisplayFields () {
      if (this.entityDisplayFields.length > 0) {
        return this.entityDisplayFields[this.entityIndex];
      }
      return this.displayFields;
    },
    originTotal () {
      return this.total;
    }
  },
  watch: {
    visible (val) {
      if (val) {
        this.tableFooterShadow = false;

        this.selectList = [...this.list];
        this.searchList = [];
        this.entityIndex = 0;
        this.entityLabels = this.entities.map(item => {
          return item.label;
        });

        this.entityDisplayFields = this.entities.map(item => {
          return item.displayFields;
        });

        this.entityList = this.entities.map(item => {
          return item[this.computedRequestAlias['entity']];
        });

        this.$nextTick(() => {
          if (typeof ResizeObserver === 'function') {
            this.tableSizeObserver = new ResizeObserver(() => {
              this.addTableFooterShadow();
            });
            this.tableSizeObserver.observe(
              this.$refs.table &&
                this.$refs.table.$el &&
                this.$refs.table.$el.querySelector('.el-table__body-wrapper')
            );
          }
          if (
            this.$refs.table.dataList &&
            this.$refs.table.dataList.length > 0
          ) {
            this.onTableLoadComplete();
            this.isSearch = false;
            this.selectValue = '';
          }
        });
      } else {
        if (this.tableSizeObserver) this.tableSizeObserver.disconnect();
      }
    },
    total (val) {
      this.totalRecords = val;
    }
  },

  destroyed () {
    this.timer && clearTimeout(this.timer);
    if (this.tableSizeObserver) this.tableSizeObserver.disconnect();
  },

  methods: {
    clearSelection () {
      this.clear();
    },
    closeDialog () {
      this.$refs.dialog.close();
    },
    formatItemHandler (row) {
      if (this.formatItem) {
        return this.formatItem(row);
      }
      return row[this.alias.name];
    },
    changeRadioHandler () {
      this.loadData();
    },
    clearQueryValue () {
      this.search();
    },
    clearSearchValue () {
      this.afterSearchTag();
      this.isSearch = false;
    },
    /**
     * @private
     * 搜索后选项
     */
    afterSearchTag () {
      this.timer && clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        let selection = copyObj(
          this.$refs.table &&
            this.$refs.table.$children[0] &&
            this.$refs.table.$children[0].selection
        );
        this.selectList = selection;
        this.searchList = this.selectList;
      });
    },
    // 删除已选择项
    delTag (index, item) {
      if (!this.selectValue) {
        this.selectList.splice(index, 1);
        this.searchList = this.selectList;
      } else {
        // 删除 selectList 包括 deleteSearchList 的项
        this.selectList = this.selectList.filter(
          data => item[this.alias.id] !== data[this.alias.id]
        );

        this.searchList.splice(index, 1);
      }

      this.deleteList.push(item);
      if (this.searchList.length === 0) {
        this.afterSearchTag();
        this.selectValue = '';
        this.isSearch = false;
      }

      this.$nextTick(() => {
        this.$refs.table.toggleRowSelection(item, false);
      });
    },
    // 搜索已选项
    searchSelect () {
      if (!this.selectValue) {
        this.afterSearchTag();
        this.isSearch = false;
      } else {
        this.isSearch = true;
      }
      let selected = copyObj(this.selectList);
      selected = selected.filter(item => {
        this.$refs.table.toggleRowSelection(item, true);
        return item[this.alias.name].indexOf(this.selectValue) >= 0;
      });
      this.searchList = selected;
    },
    getRowKeys (row) {
      return row[this.alias.id];
    },
    search () {
      this.loadData();
      if (this.queryValue) {
        this.totalRecords = this.tableSearchSelected().length;
      } else {
        this.handleTotalRecords();
      }
    },
    tableSearchSelected () {
      return this.selectList.filter(item =>
        item[this.alias.name].includes(this.queryValue)
      )
    },
    handleTotalRecords () {
      if (this.isChecked) {
        this.totalRecords = this.selectList.length;
      } else {
        this.totalRecords = this.originTotal;
      }
    },
    changeCheckboxHandler () {
      this.loadData();
      this.handleTotalRecords();
    },
    // 加载数据
    loadData () {
      this.$refs.table.clearSelection();
      this.$refs.table.loadData();
    },
    lodaData () {
      this.$refs.table.clearSelection();
      this.$refs.table.loadData();
    },
    addTableFooterShadow () {
      const tableElement = this.$refs.table && this.$refs.table.$el;
      const tableElementHeight =
        tableElement && tableElement.querySelector('.el-table').offsetHeight;
      const tableHeaderHeight =
        tableElement &&
        tableElement.querySelector('.el-table__header-wrapper').offsetHeight;
      const tableScrollHeight =
        tableElement &&
        tableElement.querySelector('.el-table__body-wrapper').offsetHeight;

      if (tableScrollHeight + tableHeaderHeight > tableElementHeight) {
        this.tableFooterShadow = true;
      } else {
        this.tableFooterShadow = false;
      }
    },
    fetchData ({ pageIndex = 1, pageSize = 10 }) {
      this.params[this.innerPageFields.pageIndex] = pageIndex;
      this.params[this.innerPageFields.pageSize] = pageSize;
      this.params[this.innerPageFields.queryValue] = this.queryValue;
      this.params[this.innerPageFields.entity] = this.entityList[this.entityIndex];

      if (this.isChecked) {
        let result;
        let selectList = copyObj(this.selectList);
        if (this.queryValue) {
          selectList = this.tableSearchSelected();
        } else {
          selectList = this.selectList;
        }
        if (pageIndex === 1) {
          result = selectList.slice(pageIndex - 1, pageSize);
        } else {
          result = selectList.slice(
            (pageIndex - 1) * pageSize,
            pageSize * pageIndex
          );
        }
        return Promise.resolve(result);
      }

      return this.fetch(this.params);
    },
    /**
     * @private 点击表格的checkbox
     * @param {Array} selection 所有选中项
     * @param {Object} row 当前选中项
     */
    select (selection, row) {
      this.onTableRowClick(row);
    },
    /**
     * @private 点击表格的全选
     * @param {Array} selection 所有选中项
     */
    selectAll (selection) {
      this.selectList = [...selection];
      this.searchList = [...selection];

      this.$emit('change', {
        data: copyObj(selection),
        multiple: true
      });
    },
    /**
     * @private 表格行点击
     * @param {Object} 当前点击行的数据
     */
    onTableRowClick (row, column, cell, event) {
      let selected = copyObj(this.selectList);

      if (!this.multiple) {
        if (
          selected &&
          selected[0] &&
          this.getRowKeys(selected[0]) === this.getRowKeys(row)
        ) {
          selected = null;
          this.selectList = [];
          this.$refs.table.clearSelection();
        } else {
          selected = row;
          this.$refs.table.clearSelection();
          this.$refs.table.toggleRowSelection(selected);
          this.selectList = [selected];
        }
      } else {
        if (selected && selected.length > 0) {
          const index = selected.findIndex(
            v => this.getRowKeys(v) === this.getRowKeys(row)
          );
          if (index >= 0) {
            selected.splice(index, 1);
          } else {
            selected.push(row);
          }
        } else {
          selected.push(row);
        }
        this.selectList = selected;
        this.searchList = selected;
      }

      this.$emit('change', {
        data: copyObj(selected),
        multiple: this.multiple
      });
    },
    /**
     * @private 表格数据加载完成
     */
    onTableLoadComplete () {
      if (!this.keepSelected) {
        this.selectList = [];
        this.searchList = [];
      } else {
        this.searchList = this.selectList;
        let selected = copyObj(this.selectList);

        this.$refs.table.dataList.forEach(item => {
          this.$refs.table.toggleRowSelection(
            item,
            selected.some(d => this.getRowKeys(d) === this.getRowKeys(item))
          );
        });
        this.selectList.forEach(item => {
          this.$refs.table.toggleRowSelection(item, true);
        });
      }
    },
    clear () {
      this.selectList = [];
      this.searchList = [];
      this.$refs.table && this.$refs.table.clearSelection();
      this.$emit('cancel');
    },
    confirm () {
      this.$refs.dialog.confirm();
      const selected = this.multiple ? this.selectList : this.selectList[0];
      this.$emit('confirm', selected);
    }
  }
};
</script>



// WEBPACK FOOTER //
// rtLookupView.vue?a5466ed8